import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { Spinner } from 'reactstrap'
import MaterialReactTable from 'material-react-table'
import { MRT_Localization_PT } from 'material-react-table/locales/pt'
import oikosApi from '../../services/oikos'
import { notifyError } from '../../utils/notify'
import { getOikosFileUrl } from '../../utils/linksOikos/link'
import { sectorIdsOf } from '../../utils/setorFinanceiro'
import './documentosRepositorio.css'

const ativo = (row, key) => {
  const value = row?.[key]
  return value === undefined || value === null || value === '' || Number(value) === 1
}

const nomeDocumento = (doc, arquivo) => {
  const nome = String(doc.documento_nome || '').trim()
  if (nome) return nome
  const desc = String(doc.documento_desc || '').trim()
  if (desc) return desc
  const arquivoNome = String(arquivo?.arquivo_desc || arquivo?.arquivo_caminho || '')
    .split(/[\\/]/)
    .pop()
    .trim()
  if (arquivoNome) return arquivoNome
  return `Documento ${doc.documento_id}`
}

const EXT_IMAGEM = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']
const EXT_PAGINA = ['pdf', 'txt']

const extensaoArquivo = (file) => {
  const nome = String(file?.arquivo_desc || file?.arquivo_caminho || '')
  const match = nome.split('?')[0].match(/\.([a-z0-9]+)$/i)
  return match ? match[1].toLowerCase() : ''
}

const arquivoAtual = (files) => {
  if (!files?.length) return null
  return [...files].sort((a, b) => {
    const da = String(a.arquivo_dtCria || a.arquivo_dtMod || '')
    const db = String(b.arquivo_dtCria || b.arquivo_dtMod || '')
    if (da !== db) return da < db ? 1 : -1
    return Number(b.arquivo_id) - Number(a.arquivo_id)
  })[0]
}

export default function DocumentosRepositorio({ embedded = false }) {
  const user = useSelector((state) => state.auth?.user)
  const [loading, setLoading] = useState(true)
  const [setores, setSetores] = useState([])
  const [vinculos, setVinculos] = useState([])
  const [documentos, setDocumentos] = useState([])
  const [arquivos, setArquivos] = useState([])
  const [visto, setVisto] = useState(null)

  const meusSetores = useMemo(() => new Set(sectorIdsOf(user?.pf_setoresId)), [user?.pf_setoresId])
  const eAdmin = Number(user?.perm_id) === 1

  useEffect(() => {
    if (!visto) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') setVisto(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [visto])

  useEffect(() => {
    let cancel = false
    const load = async () => {
      setLoading(true)
      try {
        const [setoresRes, vincRes, docsRes, arqRes] = await Promise.all([
          oikosApi.post('/', { action: 'get', table: 'setor', data: { setor_ativo: 1 } }),
          oikosApi.post('/', {
            action: 'get',
            table: 'setorVinc',
            data: { setorVinc_tabRef: 'documento', setorVinc_ativo: 1 },
          }),
          oikosApi.post('/', { action: 'get', table: 'documento', data: { documento_ativo: 1 } }),
          oikosApi.post('/', {
            action: 'get',
            table: 'arquivo',
            data: { arquivo_tabRef: 'documento', arquivo_ativo: 1 },
          }),
        ])
        if (cancel) return
        setSetores(Array.isArray(setoresRes.data) ? setoresRes.data : [])
        setVinculos(Array.isArray(vincRes.data) ? vincRes.data : [])
        setDocumentos(Array.isArray(docsRes.data) ? docsRes.data : [])
        setArquivos(Array.isArray(arqRes.data) ? arqRes.data : [])
      } catch (err) {
        if (!cancel) notifyError('Não foi possível carregar os documentos.')
      } finally {
        if (!cancel) setLoading(false)
      }
    }
    load()
    return () => {
      cancel = true
    }
  }, [])

  const setorNomePorId = useMemo(() => {
    const map = {}
    setores.forEach((setor) => {
      map[String(setor.setor_id)] = setor.setor_nome || `Setor ${setor.setor_id}`
    })
    return map
  }, [setores])

  const arquivosPorDoc = useMemo(() => {
    const map = {}
    arquivos.filter((arq) => ativo(arq, 'arquivo_ativo')).forEach((arq) => {
      const id = String(arq.arquivo_tabId)
      if (!map[id]) map[id] = []
      map[id].push(arq)
    })
    return map
  }, [arquivos])

  const setoresPorDoc = useMemo(() => {
    const map = {}
    vinculos
      .filter((v) => String(v.setorVinc_tabRef) === 'documento' && ativo(v, 'setorVinc_ativo'))
      .forEach((v) => {
        const id = String(v.setorVinc_tabId)
        if (!map[id]) map[id] = new Set()
        map[id].add(String(v.setor_id))
      })
    return map
  }, [vinculos])

  const rows = useMemo(() => {
    const lista = documentos.filter((doc) => ativo(doc, 'documento_ativo'))
    return lista
      .map((doc) => {
        const docId = String(doc.documento_id)
        const idsDisponiveis = new Set(setoresPorDoc[docId] || [])
        const idResponsavel = doc.setor_id != null && doc.setor_id !== ''
          ? String(doc.setor_id)
          : null

        const idsAcesso = new Set(idsDisponiveis)
        if (idResponsavel) idsAcesso.add(idResponsavel)
        if (!idsAcesso.size) return null

        const nomesDe = (ids) => [...ids]
          .map((id) => setorNomePorId[id] || `Setor ${id}`)
          .sort((a, b) => a.localeCompare(b, 'pt-BR'))

        const setorResponsavel = idResponsavel
          ? (setorNomePorId[idResponsavel] || `Setor ${idResponsavel}`)
          : '—'
        const setoresDisponiveis = idsDisponiveis.size
          ? nomesDe(idsDisponiveis).join(', ')
          : '—'

        const ePrivado = Number(doc.privacidade_id) !== 1
        const temAcessoSetor = [...idsAcesso].some((id) => meusSetores.has(id))
        const podeAbrir = !ePrivado || eAdmin || temAcessoSetor
        const file = arquivoAtual(arquivosPorDoc[docId] || [])

        const nome = nomeDocumento(doc, file)
        const descricao = String(doc.documento_desc || '').trim()
        // modelos antigos: nome e descrição eram o mesmo texto
        const descricaoExibir = descricao && descricao !== nome ? descricao : (doc.documento_nome ? descricao : '')

        return {
          ...doc,
          nome,
          descricao: descricaoExibir,
          setorResponsavel,
          setoresDisponiveis,
          setorIds: [...idsAcesso],
          ePrivado,
          podeAbrir,
          arquivo: file,
        }
      })
      .filter(Boolean)
      .sort((a, b) => {
        const setorCmp = a.setorResponsavel.localeCompare(b.setorResponsavel, 'pt-BR')
        if (setorCmp) return setorCmp
        return a.nome.localeCompare(b.nome, 'pt-BR')
      })
  }, [documentos, setoresPorDoc, setorNomePorId, arquivosPorDoc, meusSetores, eAdmin])

  const abrirArquivo = (row) => {
    if (!row?.podeAbrir || !row?.arquivo?.arquivo_caminho) return
    const file = row.arquivo
    const ext = extensaoArquivo(file)
    const url = getOikosFileUrl(file.arquivo_caminho)
    const nome = file.arquivo_desc || file.arquivo_caminho || row.nome
    if (EXT_IMAGEM.includes(ext) || EXT_PAGINA.includes(ext)) {
      setVisto({ url, nome, ext, download: true })
      return
    }
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const columns = [
    {
      accessorKey: 'nome',
      header: 'Nome do modelo',
      Cell: ({ row }) => (
        <span className={row.original.podeAbrir ? undefined : 'docs-repo__locked-label'}>
          {row.original.nome}
          {row.original.ePrivado && !row.original.podeAbrir ? ' · Privado' : ''}
        </span>
      ),
    },
    {
      accessorKey: 'descricao',
      header: 'Descrição',
      Cell: ({ row }) => (
        <span className={row.original.podeAbrir ? undefined : 'docs-repo__locked-label'}>
          {row.original.descricao || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'setorResponsavel',
      header: 'Setor responsável',
      size: 180,
    },
    {
      accessorKey: 'setoresDisponiveis',
      header: 'Setores disponíveis',
      size: 280,
    },
    {
      id: 'abrir',
      header: 'Arquivo',
      size: 140,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const { podeAbrir, arquivo } = row.original
        if (!arquivo?.arquivo_caminho) {
          return <span className="docs-repo__muted">Sem arquivo</span>
        }
        if (!podeAbrir) {
          return <span className="docs-repo__blocked" title="Documento privado — sem acesso do seu setor">Bloqueado</span>
        }
        return (
          <button
            type="button"
            className="docs-repo__open"
            onClick={(event) => {
              event.stopPropagation()
              abrirArquivo(row.original)
            }}
          >
            Abrir
          </button>
        )
      },
    },
  ]

  const repo = (
    <div className="docs-repo">
      <main className="docs-repo__main">
        <header className="docs-repo__head">
          <h1>Documentos</h1>
          <p>Repositório por setor. Documentos privados sem acesso do seu setor aparecem bloqueados.</p>
        </header>
        {loading && (
          <div className="docs-repo__loading">
            <Spinner size="sm" /> Carregando documentos…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <p className="docs-repo__empty">Nenhum documento no repositório.</p>
        )}
        {!loading && rows.length > 0 && (
          <MaterialReactTable
            columns={columns}
            data={rows}
            localization={MRT_Localization_PT}
            getRowId={(row) => String(row.documento_id)}
            enableColumnFilters
            enableGlobalFilter
            enableDensityToggle={false}
            enableExpanding={false}
            initialState={{ density: 'compact', sorting: [{ id: 'setorResponsavel', desc: false }] }}
            muiTableBodyRowProps={({ row }) => ({
              className: row.original.podeAbrir ? undefined : 'docs-repo__row--locked',
            })}
          />
        )}
      </main>
      {visto && createPortal(
        <div
          className="docs-repo__viewer"
          role="presentation"
          onClick={() => setVisto(null)}
        >
          <div
            className="docs-repo__viewer-box"
            role="dialog"
            aria-modal="true"
            aria-label={visto.nome}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="docs-repo__viewer-bar">
              <strong>{visto.nome}</strong>
              <div className="docs-repo__viewer-actions">
                <a
                  className="docs-repo__open docs-repo__open--link"
                  href={visto.url}
                  target="_blank"
                  rel="noreferrer"
                  download={visto.nome}
                >
                  Baixar
                </a>
                <button type="button" className="docs-repo__viewer-close" onClick={() => setVisto(null)}>
                  Fechar
                </button>
              </div>
            </div>
            {EXT_IMAGEM.includes(visto.ext) && (
              <img className="docs-repo__viewer-image" src={visto.url} alt={visto.nome} />
            )}
            {EXT_PAGINA.includes(visto.ext) && (
              <iframe className="docs-repo__viewer-frame" src={visto.url} title={visto.nome} />
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )

  if (embedded) return repo
  return <div className="page-content">{repo}</div>
}
